import { DarkModeRounded, LightModeRounded } from "@mui/icons-material";
import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  SvgIcon,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import React, { lazy, Suspense, useEffect, useState } from "react";
import { Menu as MenuIcon } from "react-feather";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation, useParams } from "react-router-dom";
import Hidden from "src/components/Hidden";
import Logo from "src/components/Logo";
import { THEMES } from "src/constants";
import { PLAN_FEATURES } from "src/constants/planFeatures";
import useSubscription from "src/hooks/useSubscription";
import { saveSettings } from "src/slices/settings";
import { useDispatch, useSelector } from "src/store";
import ExchangeValues from "src/views/arkadu/module/ExchangeValues";
import Account from "./Account";
import Notifications from "./Notifications";
import OnlineSupport from "./OnlineSupport";
import Search from "./Search";
import Settings from "./Settings";
// import Help from "./Help";
var configSite = window["configSite"];

const DRAWER_WIDTH = 276;

const Help = lazy(() => import("./Help"));
const TopBar = ({ onMobileNavOpen, ...rest }) => {
  const { customerName } = useParams();
  const settings = useSelector((state) => state.settings);
  const dispatch = useDispatch();
  const { currentCustomer } = useSelector((state) => state.customers);
  const { t } = useTranslation();
  const location = useLocation();
  const { user, isAuthenticated, identitySelected } = useSelector(
    (state) => state.users,
  );
  const { hasPlan, planInclude } = useSubscription();
  const isDark = settings.theme === THEMES.DARK;
  const smartNotificationsEnabled =
    hasPlan && planInclude(PLAN_FEATURES.NETS_NOTIFY.SMART_NOTIFICATIONS);

  const toggleTheme = () => {
    dispatch(
      saveSettings({ ...settings, theme: isDark ? THEMES.LIGHT : THEMES.DARK }),
    );
  };

  const [state, setState] = React.useState({
    notShowDemoAlert: localStorage.getItem("notShowDemoAlert"),
    showUpdateIdentities: localStorage.getItem("showUpdateIdentities"),
    enabledUpdateIdentities: false,
  });
  const [customerStoriesMeta, setCustomerStoriesMeta] = useState({
    hasStories: false,
    count: 0,
  });

  const resetShowUpdateIdentities = () => {
    localStorage.removeItem("showUpdateIdentities");
    setState((prevState) => ({
      ...prevState,
      showUpdateIdentities: null,
    }));
  };

  const checkUpdatePath = () => {
    if (location.pathname.startsWith("/app/account/selectIdentity/")) {
      return true;
    }
    if (location.pathname.startsWith("/app/account/identityProfile/")) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    // if two hours have passed since the user last updated their identities
    if (
      state.showUpdateIdentities &&
      Date.now() - state.showUpdateIdentities > 1000 * 60 * 60 * 2
    ) {
      resetShowUpdateIdentities();
    }
  }, [state.showUpdateIdentities]);

  useEffect(() => {
    if (
      currentCustomer &&
      user?.licenses?.length &&
      ["sancarlos", "guacara", "ciudad"].includes(currentCustomer?.shortname)
    ) {
      setState((prevState) => ({
        ...prevState,
        enabledUpdateIdentities: true,
      }));
    }
  }, [user, currentCustomer]);

  useEffect(() => {
    const handleStoriesMeta = (event) => {
      const hasStories = Boolean(event?.detail?.hasStories);
      const count = Number(event?.detail?.count || 0);
      setCustomerStoriesMeta({
        hasStories,
        count: Number.isFinite(count) ? count : 0,
      });
    };

    window.addEventListener("arkadu-customer-stories-state", handleStoriesMeta);
    return () => {
      window.removeEventListener(
        "arkadu-customer-stories-state",
        handleStoriesMeta,
      );
    };
  }, []);

  const canShowIdentityAnchor =
    !settings.adminMode &&
    Boolean(identitySelected) &&
    !location.pathname.startsWith("/app/account/selectIdentity/");

  const activeIdentityLabel =
    identitySelected?.display_name ||
    identitySelected?.legal_name ||
    t("Identidad activa");

  const activeIdentityRelationLabel =
    identitySelected?.relation_type === "shared" ? t("Compartida") : t("Dueño");

  const activeIdentityRoleLabel = (() => {
    const roleMap = {
      owner: t("Propietario"),
      view: t("Lector"),
      full: t("Editor"),
      admin: t("Administrador"),
    };

    return (
      roleMap[String(identitySelected?.role || "owner").toLowerCase()] ||
      identitySelected?.role ||
      t("Propietario")
    );
  })();

  const activeIdentityMeta = [
    identitySelected?.national_id || identitySelected?.short_id || "",
    identitySelected?.role !== "owner" ? activeIdentityRoleLabel : "",
    identitySelected?.relation_type === "shared"
      ? activeIdentityRelationLabel
      : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <AppBar
      position="fixed"
      color="default"
      elevation={0}
      sx={{
        borderBottom: "1px solid",
        borderColor: (theme) =>
          settings.adminMode
            ? alpha(
                theme.palette.adminIndicator?.main ??
                  theme.palette.secondary.main,
                theme.palette.mode === "dark" ? 0.62 : 0.44,
              )
            : theme.palette.divider,
        backdropFilter: (theme) =>
          theme.palette.mode === "dark" ? "none" : "blur(14px)",
        // En desktop, el AppBar comienza después del sidebar para que la fecha/hora no quede oculta
        left: { lg: `${DRAWER_WIDTH}px` },
        width: { lg: `calc(100% - ${DRAWER_WIDTH}px)` },
        backgroundColor: (theme) =>
          settings.adminMode
            ? theme.palette.mode === "dark"
              ? alpha(
                  theme.palette.navbar?.dark ?? theme.palette.background.dark,
                  0.95,
                )
              : alpha(
                  theme.palette.navbar?.main ?? theme.palette.primary.main,
                  0.94,
                )
            : theme.palette.mode === "dark"
              ? (theme.palette.navbar?.dark ?? theme.palette.background.default)
              : (theme.palette.navbar?.main ?? "rgba(255, 255, 255, 0.84)"),
        boxShadow: (theme) =>
          settings.adminMode
            ? `inset 0 2px 0 ${alpha(
                theme.palette.adminIndicator?.main ??
                  theme.palette.secondary.main,
                theme.palette.mode === "dark" ? 0.9 : 0.65,
              )}`
            : "none",
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: (theme) =>
            settings.adminMode
              ? `linear-gradient(90deg, ${alpha(
                  theme.palette.adminIndicator?.main ??
                    theme.palette.secondary.main,
                  theme.palette.mode === "dark" ? 0.2 : 0.14,
                )} 0%, ${alpha(
                  theme.palette.adminIndicator?.main ??
                    theme.palette.secondary.main,
                  0.06,
                )} 100%)`
              : "none",
          opacity: settings.adminMode ? 1 : 0,
        },
      }}
    >
      <Toolbar
        sx={{
          position: "relative",
          zIndex: 1,
          minHeight: { xs: 56, md: 60 },
          px: { xs: 1, md: 2 },
          gap: 1,
        }}
      >
        <Hidden lgUp>
          <IconButton color="inherit" onClick={onMobileNavOpen} size="large">
            <SvgIcon fontSize="small">
              <MenuIcon />
            </SvgIcon>
          </IconButton>
        </Hidden>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            minWidth: 0,
          }}
        >
          {/* Logo: solo en mobile (en desktop está en el sidebar) */}
          <Hidden lgUp>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                px: 1.25,
                py: 0.75,
                borderRadius: 99,
                border: customerStoriesMeta.hasStories
                  ? "2px solid"
                  : "2px solid transparent",
                borderColor: customerStoriesMeta.hasStories
                  ? "primary.main"
                  : "transparent",
                bgcolor: (theme) =>
                  alpha(
                    theme.palette.background.paper,
                    theme.palette.mode === "dark" ? 0.94 : 0.72,
                  ),
                boxShadow: (theme) =>
                  `0 8px 20px ${alpha(
                    theme.palette.common.black,
                    theme.palette.mode === "dark" ? 0.22 : 0.08,
                  )}`,
                cursor: customerStoriesMeta.hasStories ? "pointer" : "default",
              }}
              onClick={() => {
                if (!customerStoriesMeta.hasStories) {
                  return;
                }
                window.dispatchEvent(
                  new CustomEvent("arkadu-open-customer-stories", {
                    detail: { index: 0, source: "topbar-logo" },
                  }),
                );
              }}
            >
              <RouterLink
                to={
                  currentCustomer &&
                  !window.location.hostname.endsWith("arkadu.com")
                    ? `/${currentCustomer.shortname}`
                    : isAuthenticated
                      ? window["APP_HOME"]
                      : `/`
                }
              >
                <Logo />
              </RouterLink>
            </Box>
          </Hidden>

          {/* Fecha/hora: siempre visible — en desktop queda bien ya que el AppBar arranca después del sidebar */}
          <Box
            sx={{
              display: { xs: "none", sm: "flex" },
              flexDirection: "column",
              justifyContent: "center",
              minWidth: 0,
            }}
          >
            <Typography
              variant="body2"
              color="inherit"
              sx={{
                textTransform: "capitalize",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: { sm: 260, lg: 480 },
                fontWeight: 500,
                color: (theme) =>
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.common.white, 0.94)
                    : theme.palette.text.primary,
              }}
            >
              {format(
                new Date(
                  new Date().toLocaleString("en-US", {
                    timeZone: currentCustomer?.timezone || "America/Caracas",
                  }),
                ),
                "EEEE, dd-MM-yyyy hh:mm a",
                {
                  locale: es,
                },
              )}
            </Typography>
          </Box>
          {/* Tasas de cambio compactas */}
          {currentCustomer?.currencies?.length > 0 && (
            <Box sx={{ display: { xs: "none", md: "flex" }, ml: 1 }}>
              <ExchangeValues mode="topbar" />
            </Box>
          )}
        </Box>
        <Box sx={{ ml: 2, flexGrow: 1 }} />

        {/* {!checkUpdatePath() &&
          state.enabledUpdateIdentities &&
          !state.showUpdateIdentities && (
            <>
              <Typography variant="h6" color="inherit">
                {t("Es necesario actualizar su información.")}
              </Typography>{" "}
              <Button
                variant="contained"
                color="warning"
                href={"/app/account/selectIdentity/"}
                sx={{
                  ml: 2,
                }}
              >
                {t("Actualizar información")}
              </Button>
              <Box flexGrow={1} />
            </>
          )} */}

        {/* if host != arkadu */}

        {/* <AndroidApp /> */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.25, md: 0.5 },
            px: { xs: 0.25, md: 0.75 },
            py: 0.25,
            borderRadius: 99,
            bgcolor: (theme) =>
              alpha(
                theme.palette.mode === "dark"
                  ? theme.palette.background.default
                  : theme.palette.background.paper,
                theme.palette.mode === "dark" ? 1 : 0.72,
              ),
            boxShadow: (theme) =>
              `0 10px 24px ${alpha(
                theme.palette.common.black,
                theme.palette.mode === "dark" ? 0.2 : 0.06,
              )}`,
          }}
        >
          {canShowIdentityAnchor && (
            <Tooltip
              title={
                identitySelected?.relation_type === "shared" &&
                identitySelected?.owner?.email
                  ? `${t("Cambiar o configurar identidad")} · ${t("Propietario")}: ${identitySelected.owner.email}`
                  : t("Cambiar o configurar identidad")
              }
            >
              <Button
                color="inherit"
                href="/app/account/selectIdentity/"
                sx={{
                  display: { xs: "none", md: "inline-flex" },
                  maxWidth: 236,
                  minWidth: 0,
                  borderRadius: 99,
                  px: 1.25,
                  py: 0.45,
                  textTransform: "none",
                  border: "1px solid",
                  borderColor: (theme) => alpha(theme.palette.divider, 0.8),
                  bgcolor: (theme) =>
                    alpha(
                      theme.palette.background.paper,
                      theme.palette.mode === "dark" ? 0.22 : 0.78,
                    ),
                }}
              >
                <Box sx={{ minWidth: 0, textAlign: "left" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: { md: 160, lg: 200 },
                      lineHeight: 1.05,
                    }}
                  >
                    {activeIdentityLabel}
                  </Typography>
                  {activeIdentityMeta && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: { md: "none", lg: "block" },
                        lineHeight: 1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 200,
                        opacity: 0.72,
                      }}
                    >
                      {activeIdentityMeta}
                    </Typography>
                  )}
                </Box>
              </Button>
            </Tooltip>
          )}
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <Suspense fallback={<CircularProgress size={18} />}>
              <Help />
            </Suspense>
          </Box>
          <OnlineSupport />
          {settings.adminMode && (
            <Box sx={{ display: { md: "block" } }}>
              <Search />
            </Box>
          )}
          {smartNotificationsEnabled && <Notifications />}

          <Tooltip title={isDark ? t("Modo claro") : t("Modo oscuro")}>
            <IconButton
              color="inherit"
              onClick={toggleTheme}
              size="large"
              aria-label="toggle theme"
              sx={{
                display: { xs: "none", md: "inline-flex" },
                color: (theme) =>
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.common.white, 0.96)
                    : "inherit",
              }}
            >
              {isDark ? (
                <LightModeRounded fontSize="small" />
              ) : (
                <DarkModeRounded fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          <Settings />

          <Box sx={{ ml: { xs: 0.5, md: 1 }, flexShrink: 0 }}>
            <Account />
          </Box>
        </Box>
      </Toolbar>

      <Hidden mdUp>
        <Box
          sx={{
            position: "fixed",
            right: 10,
            top: 66,
            zIndex: (theme) => theme.zIndex.appBar + 2,
          }}
        >
          <Tooltip title={isDark ? t("Modo claro") : t("Modo oscuro")}>
            <IconButton
              color="inherit"
              onClick={toggleTheme}
              aria-label="toggle theme floating"
              sx={{
                width: 38,
                height: 38,
                border: "1px solid",
                borderColor: (theme) => alpha(theme.palette.divider, 0.8),
                bgcolor: (theme) =>
                  alpha(
                    theme.palette.background.paper,
                    theme.palette.mode === "dark" ? 0.95 : 0.9,
                  ),
                boxShadow: (theme) =>
                  `0 8px 18px ${alpha(
                    theme.palette.common.black,
                    theme.palette.mode === "dark" ? 0.24 : 0.12,
                  )}`,
              }}
            >
              {isDark ? (
                <LightModeRounded fontSize="small" />
              ) : (
                <DarkModeRounded fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Hidden>

      {(!state.notShowDemoAlert ||
        Date.now() - state.notShowDemoAlert > 1000 * 60 * 60 * 72) &&
        configSite?.siteDemo && (
          <Toolbar
            sx={{
              minHeight: 40,
              gap: 1,
              px: 2,
              borderTop: "1px solid",
              borderColor: "warning.main",
              bgcolor: "warning.main",
              color: "warning.contrastText",
            }}
          >
            <Typography
              variant="body2"
              color="inherit"
              sx={{ fontWeight: 600 }}
            >
              {t(
                "Está en modo demo, todos los datos se cargan en el servidor de pruebas y serán eliminados en 72 horas",
              )}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                localStorage.setItem("notShowDemoAlert", Date.now());
                setState({ ...state, notShowDemoAlert: Date.now() });
              }}
            >
              {t("No mostrar durante 72 horas")}
            </Button>
          </Toolbar>
        )}
      {settings.adminMode && window["SUSPENDED_ACCOUNT"] && (
        <Toolbar
          sx={{
            minHeight: 44,
            px: 2,
            borderTop: "1px solid",
            borderColor: "error.main",
            bgcolor: "error.main",
            color: "error.contrastText",
          }}
        >
          <Box>
            <Typography
              variant="subtitle2"
              color="inherit"
              sx={{ fontWeight: 700 }}
            >
              {t("Atención: su cuenta presenta pagos pendientes.")}
            </Typography>
            <Typography variant="caption" color="inherit">
              {t(
                "Regularice su saldo para mantener el acceso administrativo y evitar la suspensión automática en 2 días.",
              )}
            </Typography>
          </Box>
        </Toolbar>
      )}
      {/* on small screens show date in format Lunes, 24-01-2025 10:15AM under logo */}
      <Hidden smUp>
        <Toolbar
          className={""}
          sx={{
            justifyContent: "center",
            padding: 0,
            height: "0.9rem",
            minHeight: "0.9rem !important",
            boxShadow: "none !important",
          }}
        >
          <Typography
            variant="body2"
            color="inherit"
            sx={{
              textTransform: "capitalize",
              marginLeft: 2,
              paddingTop: 0.5,
              display: { xs: "block", sm: "none" },
              fontSize: "0.8rem",
              color: (theme) =>
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.common.white, 0.92)
                  : theme.palette.text.primary,
            }}
          >
            {format(
              new Date(
                new Date().toLocaleString("en-US", {
                  timeZone: currentCustomer?.timezone || "America/Caracas",
                }),
              ),
              "EEEE, dd-MM-yyyy hh:mm a",
              {
                locale: es,
              },
            )}
          </Typography>
        </Toolbar>
      </Hidden>
    </AppBar>
  );
};

TopBar.defaultProps = {
  onMobileNavOpen: () => {},
};

export default TopBar;
